import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import {
  actionNamespaceSelectComponentHierarchyPane,
  SELECT_COMPONENT_HIERARCHIES_PANE_ID,
} from '@ardoq/global-consts';
import { type I18nLocale } from '@ardoq/i18n';
import { Locale } from '@ardoq/locale';
import { Navigator } from '../components/Navigator';
import { LoadDataError } from '../components/LoadDataError';
import { LoadDataSkeleton } from '../components/LoadDataSkeleton';
import { EmptyFilterResult } from '../components/EmptyFilterResult';
import { useResizeObserver } from '@ardoq/hooks';
import { dispatchAction } from '@ardoq/rxbeach';
import { containerHeightChanged } from '../actions/navigatorActions';
import { debounce } from 'lodash';
import { getSelectComponentHierarchyPaneConfiguration } from './selectComponentHierarchyPaneConfiguration';

import { getOnKeyDown } from './components/onKeyDown';

const dispatchContainerHeightChanged = () =>
  dispatchAction(
    containerHeightChanged(),
    actionNamespaceSelectComponentHierarchyPane
  );

const debouncedHeightChanged = debounce(dispatchContainerHeightChanged, 16);

const Root = styled.div<{ $isHidden: boolean }>`
  ${({ $isHidden }) => $isHidden && 'display: none;'}
  background-color: ${colors.grey50};
  color: ${colors.grey900};
  flex: 1;
  overflow: auto;
  position: relative;
  height: 100%;
`;

const onKeyDown = getOnKeyDown(actionNamespaceSelectComponentHierarchyPane);

type SelectComponentPaneRootProps = {
  i18nLocale: I18nLocale;
  locale: Locale;
  onCreateNewComponent?: (name: string) => void;
  hasHoverPreview: boolean;
  isMultiselectable: boolean;
  isAsyncSearch: boolean;
  areNonMatchingResultsSelectable: boolean;
  isHidden: boolean;
};

const SelectComponentPaneRoot = React.memo(
  ({
    i18nLocale,
    locale,
    onCreateNewComponent,
    hasHoverPreview,
    isMultiselectable,
    isAsyncSearch,
    areNonMatchingResultsSelectable,
    isHidden,
  }: SelectComponentPaneRootProps) => {
    const rootRef = useRef<HTMLDivElement>(null);

    useResizeObserver(rootRef, () => {
      if (rootRef.current) {
        debouncedHeightChanged();
      }
    });

    const createComponentRef = useRef(onCreateNewComponent);
    useEffect(() => {
      createComponentRef.current = onCreateNewComponent;
    }, [onCreateNewComponent]);
    const hasCreateComponent = Boolean(onCreateNewComponent);
    const onCreateComponentInternal = useMemo(
      () =>
        hasCreateComponent
          ? (name: string) => {
              createComponentRef.current?.(name);
            }
          : undefined,
      [hasCreateComponent]
    );

    const navigatorConfiguration = useMemo(
      () =>
        getSelectComponentHierarchyPaneConfiguration({
          locale,
          onCreateNewComponent: onCreateComponentInternal,
          i18nLocale,
          hasHoverPreview,
          isMultiselectable,
          isAsyncSearch,
          areNonMatchingResultsSelectable,
        }),
      [
        i18nLocale,
        locale,
        onCreateComponentInternal,
        hasHoverPreview,
        isMultiselectable,
        isAsyncSearch,
        areNonMatchingResultsSelectable,
      ]
    );

    return (
      <Root
        className={SELECT_COMPONENT_HIERARCHIES_PANE_ID}
        ref={rootRef}
        $isHidden={isHidden}
        onKeyDown={onKeyDown}
      >
        <Navigator
          navigatorConfiguration={navigatorConfiguration}
          LoadDataError={LoadDataError}
          LoadDataNotification={LoadDataSkeleton}
          EmptyFilterResult={EmptyFilterResult}
        />
      </Root>
    );
  }
);

export { SelectComponentPaneRoot };
